/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Card, Col, Form, Input, Radio, Row, Select, Space, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';

// misc imports
import useTheme from '../../../../utils/hooks/useTheme';
import { IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED } from '../../../../constants/env';
import { FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, FORM_CONFIGURATION_WEBCHAT_ALLOWED_ORIGINS_PATH } from '../../../../constants/deploymentFormPaths';

const BotDeploymentsWebchatStrategyGeneralForm = ({
  form,
  hasEditAccess,
  isCreateMode = false,
  applicationSettings,
  setApplicationSettings
}) => {
  const { t } = useTranslation();
  const { formProps } = useTheme();

  return (
    <>
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Allowed Origins')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Restrict which websites can embed this webchat deployment. Leave empty to allow all origins.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            label={
              <Typography.Text>
                {t('Allowed Origins')}

                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('Enter origins like https://example.com or *.example.com for wildcard subdomain matching')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Typography.Text>
            }
            name={FORM_CONFIGURATION_WEBCHAT_ALLOWED_ORIGINS_PATH}
          >
            {
              hasEditAccess ? (
                <Select
                  mode="tags"
                  tokenSeparators={[',']}
                  placeholder={t('e.g. https://example.com')}
                  notFoundContent={null}
                />
              ) : (
                <Typography.Text>
                  {(form.getFieldValue(FORM_CONFIGURATION_WEBCHAT_ALLOWED_ORIGINS_PATH) || []).join(', ')}
                </Typography.Text>
              )
            }
          </Form.Item>
        </Col>
      </Row>

      {IS_WEBCHAT_DEPLOYMENT_APPLICATION_SETTINGS_ENABLED && (
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Row>
              <Col span={24} flex="auto">
                <Typography.Text>
                  {t('Application Settings')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Configure the application settings')}
                </Typography.Paragraph>
              </Col>

              <Col span={24}>
                <Row gutter={formProps.rowGutter} className="align-items-baseline">
                  <Col span={24}>
                    <WarningOutlined className="text-warning " /> {
                      isCreateMode ? (
                        <Typography.Paragraph className="fs-sm d-inline" type="secondary">
                          {t('Important note: After clicking \'Create Deployment\' you will not be able to edit the Webchat app settings for this deployment.')}
                        </Typography.Paragraph>
                      ) : (
                        <Typography.Paragraph className="fs-sm  d-inline" type="secondary">
                          <span className="fw-bold">{t('Webchat app selection')}</span>
                          <span className="ml-1">{t('is possible only at initial deployment creation or clone.')}</span>
                        </Typography.Paragraph>
                      )
                    }
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item>
              <Radio.Group disabled={!isCreateMode || !hasEditAccess} className="mb-1 " value={applicationSettings} onChange={(e) => setApplicationSettings(e.target.value)}>
                <Space direction="vertical">
                  <Radio className="mb-2" value="messageApp">
                    <Typography.Text>
                      {t('Helvia Webchat App')}
                    </Typography.Text>
                  </Radio>

                  <Radio className="mb-2" value="customApp">
                    <Typography.Text>
                      {t('My own Webchat App')}
                    </Typography.Text>
                  </Radio>
                </Space>
              </Radio.Group>

              <Card
                size="small"
                className="bg-layout-gray shadow-none"
                hidden={applicationSettings !== 'customApp' && !isCreateMode}
              >
                <Form.Item
                  label={t('Client ID')}
                  name={[...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'clientId']}
                  rules={[{
                    required: applicationSettings !== 'messageApp',
                    message: t('Please input Client ID')
                  }]}
                >
                  {
                    hasEditAccess ? (
                      <Input
                        disabled={applicationSettings !== 'customApp'}
                        placeholder={t('Set client ID')} />
                    ) : (
                      <Typography.Text>
                        {form.getFieldValue([...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'clientId'])}
                      </Typography.Text>
                    )
                  }
                </Form.Item>

                <Form.Item
                  label={t('Client Secret')}
                  name={[...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'clientSecret']}
                  rules={[{
                    required: applicationSettings !== 'messageApp',
                    message: t('Please input Client Secret')
                  }]}
                >
                  {
                    hasEditAccess ? (
                      <Input
                        disabled={applicationSettings !== 'customApp'}
                        placeholder={t('Enter client secret')} />
                    ) : (
                      <Typography.Text>
                        {form.getFieldValue([...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'clientSecret'])}
                      </Typography.Text>
                    )
                  }
                </Form.Item>

                <Form.Item
                  label={t('Web Secret Key')}
                  name={[...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'webSecretKey']}
                  rules={[{
                    required: applicationSettings !== 'messageApp',
                    message: t('Please input Web Secret Key')
                  }]}
                >
                  {
                    hasEditAccess ? (
                      <Input
                        disabled={applicationSettings !== 'customApp'}
                        placeholder={t('Enter web secret key')} />
                    ) : (
                      <Typography.Text>
                        {form.getFieldValue([...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'webSecretKey'])}
                      </Typography.Text>
                    )
                  }
                </Form.Item>

                <Form.Item
                  label={t('Azure Agent Handle')}
                  name={[...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'azureBotHandle']}
                  rules={[{
                    required: applicationSettings !== 'messageApp',
                    message: t('Please input Azure Agent Handle')
                  }]}
                >
                  {
                    hasEditAccess ? (
                      <Input
                        disabled={applicationSettings !== 'customApp'}
                        placeholder={t('Enter azure agent handle')} />
                    ) : (
                      <Typography.Text>
                        {form.getFieldValue([...FORM_CONFIGURATION_WEBCHAT_INTEGRATION_PATH, 'azureBotHandle'])}
                      </Typography.Text>
                    )
                  }
                </Form.Item>
              </Card>
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};

BotDeploymentsWebchatStrategyGeneralForm.propTypes = {
  form: PropTypes.object,
  hasEditAccess: PropTypes.bool,
  isCreateMode: PropTypes.bool,
  applicationSettings: PropTypes.oneOf(['customApp', 'messageApp']),
  setApplicationSettings: PropTypes.func
};

export default BotDeploymentsWebchatStrategyGeneralForm;
